import React from "react";
import { cn } from "@/shared/lib/utils";

type StatusVariant = "info" | "success" | "warning" | "danger" | "neutral";

interface StatusBadgeProps {
  status: string;
  variant: StatusVariant;
  customColors?: {
    bg: string;
    border: string;
    text?: string;
  };
}

const variantStyles: Record<StatusVariant, string> = {
  info: 'bg-info/10 border-info text-info',
  success: 'bg-success/10 border-success text-success',
  warning: 'bg-warning/10 border-warning text-warning',
  danger: 'bg-error/10 border-error text-error',
  neutral: 'bg-surface-alt border-border-strong text-text-muted',
};

/**
 * Map well-known status labels to semantic variants so that StatusBadge is
 * theme-aware without any hardcoded hex values.  All styling is driven by
 * Tailwind token classes defined in variantStyles above.
 *
 * Callers that pass `customColors` (for truly one-off inline colours) still
 * work as before — customColors is the ONLY escape hatch.
 */
const statusVariantMap: Record<string, StatusVariant> = {
  // Client statuses
  'Active':       'info',
  'Inactive':     'neutral',
  'Anonymous':    'danger',
  'Unconfirmed':  'danger',
  'Pending':      'warning',
  'disabled':     'danger',
  // Trip statuses
  'Completed':    'success',
  'Scheduled':    'warning',
  'Canceled':     'danger',
  'Combined':     'neutral',
  'No Show':      'danger',
  'Late Cancel':  'warning',
  'Assigned':     'neutral',
  'Allocated':    'neutral',
  // Provider priority levels
  'Very high':    'danger',
  'High':         'warning',
  'Above average':'warning',
  'Medium':       'neutral',
  'Low':          'info',
  'Very low':     'neutral',
  'Below Average':'success',
};

function StatusBadgeComponent({
  status,
  variant,
  customColors,
}: StatusBadgeProps): React.ReactElement {
  // customColors is the only escape hatch for truly one-off inline colours.
  if (customColors) {
    return (
      <span
        className="inline-flex items-center rounded-[4px] border px-[8px] py-[3px] text-[12px] font-medium"
        aria-label={`Status: ${status}`}
        style={{
          backgroundColor: customColors.bg,
          borderColor: customColors.border,
          color: customColors.text ?? 'inherit',
        }}
      >
        {status}
      </span>
    );
  }

  // Resolve variant: prefer the well-known status map, then fall back to the
  // caller-supplied variant prop.
  const resolvedVariant = statusVariantMap[status] ?? variant;

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-[4px] border px-[8px] py-[3px] text-[12px] font-medium whitespace-nowrap",
        variantStyles[resolvedVariant],
      )}
      aria-label={`Status: ${status}`}
    >
      {status}
    </span>
  );
}

StatusBadgeComponent.displayName = 'StatusBadge';

export const StatusBadge = StatusBadgeComponent;